import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { URLS } from '../../API/API endpoints'
import moment from "moment";
import { DatePicker } from "antd";
import dayjs from 'dayjs';
import LoadingSpinner from '../CustomWidgets/Laoding'

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

function PhoneInput({ label, value, onChange, error, inputRef }) {
  const { i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  return (
    <div className="mt-4 mb-2">
      <div className="mb-2 font-medium">{label}</div>
      <div
        className={`flex border border-solid rounded-xl overflow-hidden justify-center items-start ${isAr ? "flex-row-reverse" : ""}`}
      >
        <span className="pl-3 py-3 bg-white text-black">
          {isAr ? "20+" : "+20"}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={onChange}
          className={`p-3 rounded-xl border-0 border-neutral-400 border-opacity-50 text-black w-full focus:outline-none bg-white ${error ? "border border-red-500" : ""}`}
          style={{ direction: "ltr" }}
          maxLength={11}
          autoComplete="off"
          autoFocus
        />
      </div>
      {error && <span className="text-red-500 text-xs mt-1">{error}</span>}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  readOnly,
  error,
  inputRef,
  onKeyDown,
}) {
  return (
    <div className="mt-4 mb-2">
      <div className="mb-2 font-medium">{label}</div>
      <div className="flex border border-solid rounded-xl overflow-hidden justify-center items-start">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          className={`p-3 rounded-xl border-0 border-neutral-400 border-opacity-50 text-black w-full focus:outline-none ${readOnly ? "bg-gray-100 cursor-not-allowed" : "bg-white"} ${error ? "border border-red-500" : ""}`}
          autoComplete="off"
        />
      </div>
      {error && <span className="text-red-500 text-xs mt-1">{error}</span>}
    </div>
  );
}

function AddressInput({ label, value, onChange, placeholder }) {
  return (
    <div className="flex flex-col">
      <div className="mt-5 mb-2 font-medium">{label}</div>
      <div className="flex border border-solid rounded-xl overflow-hidden justify-center items-start">
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="p-3 rounded-xl border-0 border-neutral-400 border-opacity-50 text-black w-full focus:outline-none bg-white"
          autoComplete="off"
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Address type config
───────────────────────────────────────────── */
const ADDRESS_TYPES = [
  { id: 1, key: "Villa", icon: "🏠" },
  { id: 2, key: "Office", icon: "🏢" },
  { id: 3, key: "Apartment", icon: "🏗️" },
];

const ADDRESS_FIELDS = {
  1: [
    { key: "addField1", labelKey: "House No. / Villa No.", placeholder: "e.g. 42" },
    { key: "addField2", labelKey: "Group No.", placeholder: "e.g. G-7" },
    { key: "addField3", labelKey: "Street", placeholder: "Street name" },
    { key: "addField4", labelKey: "Additional Info", placeholder: "Landmark or notes" },
  ],
  2: [
    { key: "addField1", labelKey: "Company", placeholder: "Company Name" },
    { key: "addField2", labelKey: "Floor", placeholder: "e.g. 3rd" },
    { key: "addField3", labelKey: "Building", placeholder: "Building name / no." },
    { key: "addField4", labelKey: "Additional Info", placeholder: "Landmark or notes" },
  ],
  3: [
    { key: "addField1", labelKey: "Building & Group", placeholder: "e.g. B2-G4" },
    { key: "addField2", labelKey: "Apartment No.", placeholder: "e.g. 12A" },
    { key: "addField3", labelKey: "Floor", placeholder: "e.g. 5th" },
    { key: "addField4", labelKey: "Additional Info", placeholder: "Landmark or notes" },
  ],
};

/* ─────────────────────────────────────────────
   Main Modal
───────────────────────────────────────────── */
function AddCustomerModal({
  onClose,
  confirm,
  showSkipButton,
  isAddressMandatory,
  editData,
  onUpdate,
  loading
}) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  const { user_id } = useSelector((state) => state?.login?.user?.result);
  const paymentReducer = useSelector((state) => state?.paymentReducer);

  const inputRef = useRef(null);

  /* form state */
  console.log(editData?.credit_as_on_date)
  const [mobile, setMobile] = useState(editData ? editData?.phone : "");
  const [displayName, setDisplayName] = useState(editData ? editData?.name : "");
  const [addField1, setaddField1] = useState(editData ? editData?.addresses[0]?.add_field_1 : "");
  const [addField2, setaddField2] = useState(editData ? editData?.addresses[0]?.add_field_2 : "");
  const [addField3, setaddField3] = useState(editData ? editData?.addresses[0]?.add_field_3 : "");
  const [addField4, setaddField4] = useState(editData ? editData?.addresses[0]?.add_field_4 : "");
  const [creditDate, setCreditDate] = useState(editData ? editData?.credit_as_on_date : "");
  const [creditAmount, setCreditAmount] = useState(editData ? editData?.credit_amount : "");

  /* UI state */
  const [selectedAddressType, setSelectedAddressType] = useState(
    editData ? { id: editData?.addresses[0]?.type_id, name: editData?.addresses[0]?.name } : null
  );
  const [addressTypes, setAddressTypes] = useState(
    ADDRESS_TYPES.map((a) => ({ id: a.id, name: t(a.key) })),
  );
  const [searchMode, setSearchMode] = useState("mobile");
  const [customerExist, setCustomerExist] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [inputDisabled, setInputDisabled] = useState(false);
  const [errors, setErrors] = useState({});

  /* payment method */
  let paymentMethod = null;
  try {
    paymentMethod =
      typeof paymentReducer === "string"
        ? JSON.parse(paymentReducer).selectedPaymentMethod
        : paymentReducer?.selectedPaymentMethod;
  } catch (_) { }

  const addressFields = selectedAddressType ? ADDRESS_FIELDS[selectedAddressType.id] : [];
  const fieldSetters = {
    addField1: setaddField1,
    addField2: setaddField2,
    addField3: setaddField3,
    addField4: setaddField4,
  };
  const fieldValues = { addField1, addField2, addField3, addField4 };

  const mobileLen = /^0/.test(mobile) ? 11 : 10;
  const isAddressEmpty = !addField1 && !addField2 && !addField3 && !addField4;

  const canConfirm =
    mobile.length >= 10 &&
    displayName.length > 0 &&
    mobile.length === mobileLen &&
    !(paymentMethod?.name === "Delivery" && isAddressEmpty);

  const CreateCustomer = async (values) => {
    confirm({
      userName: values.name,
      mobile: values.mobile.replace(/^0+/, ""),
      address_type_id: values.address_type,
      add_field_1: values.addField1,
      add_field_2: values.addField2,
      add_field_3: values.addField3,
      add_field_4: values.addField4,
      credit_amount: values.credit_amount,
      credit_as_on_date: values.credit_as_on_date,
    });
  };

  const UpdateCustomer = async (values) => {
    onUpdate({
      customer_id: editData?.id,
      userName: values.name,
      mobile: values.mobile.replace(/^0+/, ""),
      address_type_id: values.address_type,
      add_field_1: values.addField1,
      add_field_2: values.addField2,
      add_field_3: values.addField3,
      add_field_4: values.addField4,
      credit_amount: values.credit_amount,
      credit_as_on_date: values.credit_as_on_date,
    });
  };

  /* ── handlers ── */
  const handleConfirm = async () => {
    const errs = {};
    if (mobile.length < 10) errs.mobile = t("Please enter a valid phone number");
    if (isAddressMandatory && !selectedAddressType?.id) errs.address = t("Please select an address type");
    if (selectedAddressType?.id && (!addField1 || !addField2 || !addField3))
      errs.addressFields = t("Please fill in all required address fields");
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    try {
      const values = {
        mobile,
        name: displayName,
        address_type: selectedAddressType?.id,
        addField1,
        addField2,
        addField3,
        addField4,
        credit_amount: creditAmount,
        credit_as_on_date: creditDate ? dayjs(creditDate).format("YYYY-MM-DD") : "",
      };
      if (editData?.id) {
        await UpdateCustomer(values);
      } else {
        await CreateCustomer(values);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSuggestionClick = (customer) => {
    setMobile(customer.phone || "");
    setDisplayName(customer.name || "");
    setCustomerExist(true);
    setPartnerId(customer.id);
    const addresses = customer.addresses || [];
    if (addresses.length > 0 && addresses[0]?.type_id !== false) {
      setAddressTypes(addresses.map((a) => ({ id: a.type_id, name: t(a.name) })));
      const def = addresses[0];
      setSelectedAddressType({ id: def.type_id, name: def.name });
      setaddField1(def.add_field_1 || "");
      setaddField2(def.add_field_2 || "");
      setaddField3(def.add_field_3 || "");
      setaddField4(def.add_field_4 || "");
    } else {
      setAddressTypes(ADDRESS_TYPES.map((a) => ({ id: a.id, name: t(a.key) })));
      setaddField1("");
      setaddField2("");
      setaddField3("");
      setaddField4("");
    }
    setInputDisabled(true);
  };

  const clearData = () => {
    setMobile("");
    setDisplayName("");
    setCustomerExist(false);
    setPartnerId("");
    setaddField1("");
    setaddField2("");
    setaddField3("");
    setaddField4("");
    setAddressTypes(ADDRESS_TYPES.map((a) => ({ id: a.id, name: t(a.key) })));
    setSelectedAddressType(null);
    setInputDisabled(false);
  };

  const handleSkip = () => {
    onClose();
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleDateChange = (date, dateString) => {
    console.log(dateString)
    setCreditDate(dateString);
  };

  /* ─────────────────────────────────────────────
     Render
  ───────────────────────────────────────────── */
  return (
    <>
      {loading && <LoadingSpinner />}
      <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center z-50 backdrop-blur-md drop-shadow-sm">
        <div className="fixed top-0 left-0 w-full h-full bg-black opacity-50 z-40" />
        <section
          className="flex flex-col font-semibold leading-6 bg-white rounded-[25px] text-zinc-800 z-40 px-12 py-12 max-md:px-5 text-lg relative overflow-y-auto"
          style={{
            width: "1000px",
            maxHeight: "90vh",
            maxWidth: "95vw",
            direction: isAr ? "rtl" : "ltr",
          }}
        >
          {/* ── Header ── */}
          <header className="flex gap-5 text-3xl max-md:flex-wrap max-md:max-w-full mb-2">
            <h1 className="my-auto flex-auto text-3xl">
              {editData ? t("Update Customer") : t("Add Customer")}
            </h1>
            <button
              type="button"
              onClick={onClose}
              className="text-zinc-800 absolute top-5 right-5 bg-transparent"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          {/* ── Body ── */}
          <main>
            <div className="flex justify-start mt-2 mb-2">{t("Customer details")}</div>

            {/* Contact Details */}
            <div className="flex gap-6">
              <div className="flex-1">
                <PhoneInput
                  inputRef={inputRef}
                  label={t("Enter phone number")}
                  value={mobile}
                  error={errors.mobile}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    const max = /^0/.test(v) ? 11 : 10;
                    if (v.length <= max) setMobile(v);
                  }}
                />
              </div>

              <div className="flex-1">
                <TextInput
                  label={t("Enter Name")}
                  value={displayName}
                  error={errors.name}
                  readOnly={false}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t("Customer name")}
                />
              </div>
            </div>
            {inputDisabled && (
              <span
                className="flex justify-end font-normal text-blue-900 cursor-pointer mt-2 me-2"
                onClick={clearData}
              >
                {t("Clear")}
              </span>
            )}

            {/* Credit Details */}
             
              <>
                <div className="h-px bg-gray-100 my-5" />
                <span className="flex justify-start mt-2 mb-4">{t("Credit details")}</span>
                <div className="flex gap-6 mt-4">
                  <div className="flex-1 text-md">
                    <div className="mb-2 font-medium text-md">
                      {t("Date")}
                    </div>

                    <DatePicker
                      style={{
                        width: "100%",
                        height: "56px",
                        borderRadius: "12px",
                        fontSize: "20px",
                        color:"gray"
                      }}
                      size="large"
                      value={creditDate ? dayjs(creditDate): null}
                      className="mt-1 block w-full rounded-md  font-semibold flex justify-center items-center px-3 py-2 text-xl"
                      onChange={handleDateChange}
                      placeholder={t("Select Date")}
                      disabledDate={(current) =>
                       
                        current && current > moment().endOf("day")
                      }

                    />
                  </div>

                  <div className="flex-1">
                    <div className="mb-2 font-medium text-md">
                      {t("Amount")}
                    </div>

                    <input
                      type="text"
                      value={creditAmount}
                      readOnly={
                        editData &&
                        editData?.credit_amount &&
                        editData?.credit_amount > 0
                      }
                      onChange={(e) => {
                        let val = e.target.value
                          .replace(/[^0-9.]/g, "")
                          .replace(/(\..*?)\..*/g, "$1");

                        if (val.length > 10) return;
                        setCreditAmount(val);
                      }}
                      placeholder={t("Enter Amount")}
                      className="w-full h-[56px] px-4 rounded-xl border border-gray-300 text-base focus:outline-none"
                    />
                  </div>
                </div>
              </>
            

            {/* Address */}
            <div className="h-px bg-gray-100 my-5" />
            <span className="flex justify-start mt-2">
              {t("Add Address")}
            </span>
            {errors.address && (
              <p className="text-red-500 text-xs mt-1">{errors.address}</p>
            )}


            {/* Address type buttons — styled like old modal */}
            <div className="flex justify-start mt-2">
              {addressTypes.map((addr) => {
                const icon = ADDRESS_TYPES.find((a) => a.id === addr.id)?.icon || "📍";
                const isActive = selectedAddressType?.id === addr.id;
                return (
                  <button
                    key={addr.id}
                    onClick={() => {
                      if (isActive) {
                        setSelectedAddressType(null);
                        return;
                      }
                      setSelectedAddressType(addr);
                      if (!customerExist) {
                        setaddField1("");
                        setaddField2("");
                        setaddField3("");
                        setaddField4("");
                      }
                    }}
                    className={`px-4 py-2 rounded-lg border outline-none focus:outline-none bg-transparent flex items-center me-4 ${isActive
                      ? "text-[#1B4597] border-[#1B4597] border-solid"
                      : "bg-white border border-solid border-stone-300 text-zinc-600"
                      }`}
                  >
                    <span className="me-2">{icon}</span>
                    {t(addr.name)}
                  </button>
                );
              })}
            </div>

            {/* Address fields */}
            {selectedAddressType && (
              <>
                {errors.addressFields && (
                  <p className="text-red-500 text-xs mt-2">{errors.addressFields}</p>
                )}
                {/* Render pairs in rows of 2, matching old modal layout */}
                {addressFields?.reduce((rows, field, i) => {
                  if (i % 2 === 0) rows.push([field]);
                  else rows[rows.length - 1].push(field);
                  return rows;
                }, []).map((pair, rowIdx) => (
                  <div key={rowIdx} className="flex">
                    {pair.map((field, colIdx) => (
                      <div
                        key={field.key}
                        className={colIdx === 0 && pair.length > 1 ? (isAr ? "ml-4 flex-1" : "mr-4 flex-1") : "flex-1"}
                      >
                        <AddressInput
                          label={t(field.labelKey)}
                          placeholder={t(field.placeholder)}
                          value={fieldValues[field.key]}
                          onChange={(e) => fieldSetters[field.key](e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}
          </main>

          {/* ── Footer ── */}
          <footer className="flex justify-between gap-0 items-center">
            {showSkipButton && (
              <button
                onClick={handleSkip}
                className="justify-center items-center mt-6 px-16 py-3.5 text-xl text-center text-[#7EC242] rounded-lg border-2 border-[#7EC242] border-solid capitalize whitespace-nowrap bg-white me-3.5"
                style={{ width: "500px" }}
              >
                {t("Cancel")}
              </button>
            )}
            <button
              disabled={!canConfirm || loading}
              onClick={handleConfirm}
              className="justify-center mt-6 items-center px-16 py-3.5 text-xl text-center text-white capitalize whitespace-nowrap bg-[#7EC242] rounded-lg max-md:px-5 max-md:max-w-full"
              style={{
                width: "600px",
                opacity: canConfirm && !loading ? 1 : 0.5,
              }}
            >
              {loading ? (
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "2.5px solid rgba(255,255,255,0.3)",
                    borderTopColor: "#fff",
                    display: "inline-block",
                    animation: "spin 0.7s linear infinite",
                  }}
                />
              ) : (
                t("Confirm")
              )}
            </button>
          </footer>
        </section>
      </div>
    </>
  );
}

export default AddCustomerModal;